import { ILocalizedOption, IOption } from "entities";

export enum FORM_SECTION {
  DEPARTMENT = 1,
  DOCTOR = 2,
  SERVICE = 3,
  DATE_TIME = 4,
  PATIENT_FORM = 5,
  NULL = 6,
  SPECIALTY = 7,
}

export enum FORM_FLOW {
  DEFAULT = 1,
  DOCTOR_LAST,
}

export interface BookingOnLoadReqConfig {
  cmsSiteId: string;
}

export interface ServiceItem extends ILocalizedOption {
  minDate: string;
  maxDate: string;
  buildingFloor?: ILocalizedOption;
}

export interface CareProvider extends IOption {
  code: string;
  imgPath: string;
  position: string[];
  title: string[];
  qualifications: string[];
  serviceItems?: ServiceItem[];
}

export interface Department extends IOption {
  hid: string;
  flow: FORM_FLOW;
  careProviders?: CareProvider[];
  notes?: string;
}

export interface BookingOnLoadRes {
  departmentOptions?: Department[];
  enc: boolean;
  /**
   * Localized by the API according to the request lang.
   * Weather notices accompany the form when booking options remain available.
   * In that case, the API must return only options that remain bookable.
   * Other nonempty messages are displayed instead of the booking form.
   */
  systemMessage?: string;
}

export interface GetAvailableTimeSlotsReqConfig {
  serviceItemHid: string;
  careProviderHid: string;
  fromDt: Date;
  toDt: Date;
}

export interface TimeSlotSubGroup {
  name: string;
  fulfillSlot: number;
  roomSlot: number;
}

export interface TimeSlotInfo {
  isDisabled: boolean;
  containsHolidayTimeslot: boolean;
  subGroups: TimeSlotSubGroup[];
}

export interface GetAvailableTimeSlotsRes {
  departmentOptions?: Department[];
  minDate: string;
  maxDate: string;
  timeSlotDictionary?: {
    [key: string]: TimeSlotInfo;
  };
}

export interface GetAvailableTimeSlotsByCareProvidersReqConfig {
  serviceItemHid: string;
  careProviderHids: string[];
  fromDt: Date;
  toDt: Date;
}

export interface GetAvailableTimeSlotsDetailReqConfig {
  serviceItemHid: string;
  careProviderHid: string;
  fromDt: Date;
}

export interface GetAvailableTimeSlotsDetailByCareProvidersReqConfig {
  serviceItemHid: string;
  careProviderHids: string[];
  fromDt: Date;
}

export interface AvailableTimeslot {
  fromDt: string;
  toDt: string;
  label: string;
  labelTc: string;
  labelSc: string;
}

export interface GetAvailableTimeSlotsDetailRes {
  availableTimeslots: AvailableTimeslot[];
}

export interface CareProviderAvailableTimeslot {
  careProviderHid: string;
  availableTimeslots: AvailableTimeslot[];
}
export interface GetAvailableTimeSlotsDetailByCareProvidersRes {
  careProviderAvailableTimeslots: CareProviderAvailableTimeslot[];
}

export interface FormValues extends PatientFormValues {
  departmentValue?: string;
  serviceItemHid?: string;
  careProviderHid?: string;
  fromDt?: string;
  toDt?: string;
}

export interface PatientFormValues {
  lastName?: string;
  firstName?: string;
  idNo?: string;
  countryCode?: string;
  mobileNo?: string;
  email?: string;
  isJoinedEhealth?: string;
  remarks?: string;
}

export interface InternalBookingAuthValues {
  accountName: string;
  password: string;
}

export interface CreateBookingReqConfig {
  departmentHid: string;
  serviceItemHid: string;
  careProviderHid: string;
  fromDt: string;
  toDt: string;
  firstName: string;
  lastName: string;
  mobileNo: string;
  countryCode: string;
  idType: string;
  idNo: string;
  email?: string;
  isJoinedEhealth: boolean;
  remarks?: string;
  otpSid?: string;
}

export interface CreateBookingResRequireOtp {
  bookingNo: null;
  isRequireOtp: true;
}

export interface CreateBookingResNoOtp {
  bookingNo: string | null;
  isRequireOtp: false;
  bookingTime: string;
  enquiryPhoneNo: string;
  locationName: string;
  locationNameSc: string;
  locationNameTc: string;
  isConfirmed: boolean;
  bookingTimeLabel: string;
  bookingTimeLabelTc: string;
  bookingTimeLabelSc: string;
}

export type CreateBookingRes =
  | CreateBookingResRequireOtp
  | CreateBookingResNoOtp;

export interface RequestOtpReqConfig {
  countryCode: string;
  mobileNo: string;
}

export interface RequestOtpRes {
  sid: string;
  countryCode: string;
  mobileNo: string;
  expiresAt: string;
  resendQuota: number;
  nextResendAvailableAt: string;
}

export interface VerifyOtpReqConfig {
  sid: string;
  otp: string;
}

export interface VerifyOtpRes {
  sid: string;
  isSuccess: boolean;
}

export interface VerifyInternalUserReqConfig extends InternalBookingAuthValues {
  countryCode: string;
  mobileNo: string;
}

export interface VerifyInternalUserRes {
  sid: string;
  isSuccess: boolean;
}

export interface RequestSecSessionReqConfig {
  k: string;
}

export interface RequestSecSessionRes {
  pk: string;
  sign?: string;
  scheme: string;
  expiresAt: string;
}

export interface SearchBookingReqConfig {
  mobileNo: string;
  countryCode: string;
  bookingNo: string;
  otpSid?: string;
}

export interface SearchBookingResRequireOtp {
  booking: null;
  isRequireOtp: true;
}

export interface Booking {
  hid: string;
  bookingNo: string;
  department: {
    value: string;
    name: string;
    nameSc: string;
    nameTc: string;
  };
  careProvider: IOption;
  fromDt: string;
  toDt: string;
  serviceItem: {
    minDate: string;
    maxDate: string;
    hid: string;
  };
  bookingTimeLabel: string;
  bookingTimeLabelTc: string;
  bookingTimeLabelSc: string;
}

export enum ErrorTypeForSearch {
  NotFound = 1,
  Cancelled,
  Expired,
  MaxChangesExceeded,
}

export interface SearchBookingResNoOtp {
  isRequireOtp: false;
  booking?: Booking;
  error?: ErrorTypeForSearch;
  notes?: string;
  enquiryPhoneNo?: string;
  isEdit?: boolean;
  isCancel?: boolean;
}

export type SearchBookingRes =
  | SearchBookingResRequireOtp
  | SearchBookingResNoOtp;

export interface EditBookingReqConfig {
  bookingNo: string;
  hid: string;
  fromDt: string;
  toDt: string;
  otpSid?: string;
}

export interface EditBookingOnLoadRes {
  enc: boolean;
  systemMessage?: string;
}

export interface EditBookingRes {
  bookingNo?: string;
  bookingTime?: string;
  enquiryPhoneNo?: string;
  locationName?: string;
  locationNameSc?: string;
  locationNameTc?: string;
  isConfirmed?: boolean;
  bookingTimeLabel?: string;
  bookingTimeLabelTc?: string;
  bookingTimeLabelSc?: string;
}

export interface CancelBookingReqConfig {
  hid: string;
  otpSid?: string;
}

export interface CancelBookingRes {
  bookingNo?: string | null;
  bookingTime?: string;
  enquiryPhoneNo: string;
  locationName?: string;
  locationNameSc?: string;
  locationNameTc?: string;
  bookingTimeLabel?: string;
  bookingTimeLabelTc?: string;
  bookingTimeLabelSc?: string;
}
